import { useCallback } from "react";
import { Formik, FormikProps } from "formik";
import { Row, Col } from "antd";
import { Form, Select, SubmitButton } from "formik-antd";
import { useTranslation } from "react-i18next";
import { Setting as SettingType } from "/app/src/models";
import { settingService } from "/app/src/services";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { buildParams } from "/app/src/helpers";
import { handlePromiseError } from "/app/src/helpers/api";
/**
 * Component to display the forms for updating the order status code
 */
export default function OrderStatusCode() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  //useQuery for getting the order status code
  const { data: notFoundOrderCode } = useQuery({
    queryKey: ["notFoundOrderCode"],
    queryFn: () => {
      return settingService
        .getAll(buildParams({ name: "notFoundOrderCode" }))
        .then((response) => {
          return response.settings[0];
        });
    },
    initialData: { value: undefined },
    select: (data: SettingType) => {
      return data;
    },
  });
  //useMutation for updating the order status code
  const { mutateAsync: updateStatusCode } = useMutation({
    mutationFn: (updateStatusCode: SettingType) => {
      return settingService
        .updateSingle(updateStatusCode.id, { value: updateStatusCode.value })
        .then(handlePromiseError);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["notFoundOrderCode"], data.setting);
    },
  });
  //useMutation for creating the order status code
  const { mutateAsync: createStatusCode } = useMutation({
    mutationFn: (createStatusCode: SettingType) => {
      return settingService
        .createSingle({
          value: createStatusCode.value,
          name: "notFoundOrderCode",
        })
        .then(handlePromiseError);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["notFoundOrderCode"], data.setting);
    },
  });
  //onSubmit handler for creating/updating the order status code
  const onSubmitStatusCode = useCallback(
    async (
      {
        value,
      }: {
        value: string;
      },
      {
        setFieldError,
      }: { setFieldError: (field: string, value: string) => void },
    ): Promise<void> => {
      if (notFoundOrderCode?.id) {
        await updateStatusCode({ id: notFoundOrderCode.id, value }).catch(
          (error) => {
            return setFieldError("value", error.errors[0]);
          },
        );
      } else {
        await createStatusCode({ value }).catch((error) => {
          return setFieldError("value", error.errors[0]);
        });
      }
    },
    [createStatusCode, updateStatusCode, notFoundOrderCode.id],
  );
  /**
   * Update order status code form
   */
  const updateStatusCodeForm: (
    props: FormikProps<{ value: string | undefined }>,
  ) => JSX.Element = useCallback(
    ({ dirty }) => (
      <Form layout="vertical">
        <Row justify="start" gutter={16}>
          <Col span={8}>
            <h3>{t("translation:order_status_code")}</h3>
            <p>{t("translation:order_status_code_description")}</p>
          </Col>
          <Col span={13}>
            <Form.Item
              className="no-label" // skipcq: JS-0394
              name="value"
            >
              <Select name="value" size="large">
                <Select.Option value="200">200</Select.Option>
                <Select.Option value="502">502</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={3}>
            <SubmitButton
              className="no-label" // skipcq: JS-0394
              type="primary"
              size="large"
              block
              disabled={!dirty}
            >
              {t("translation:save")}
            </SubmitButton>
          </Col>
        </Row>
      </Form>
    ),
    [t],
  );
  return (
    <Formik
      component={updateStatusCodeForm}
      enableReinitialize
      initialValues={{
        value: notFoundOrderCode?.value ?? "502",
      }}
      onSubmit={onSubmitStatusCode}
    />
  );
}
